import type { SubscriptionData } from '@/types/billing'

/**
 * "Which plan is running right now?" — the one question every plan-aware
 * surface actually needs, kept in one place so they cannot disagree.
 *
 * `GET /billing/subscription` returns a subscription-shaped object for three
 * different situations, and only the dates tell them apart:
 *
 *   - a plan that is running and renews          (`auto_renew: true`)
 *   - a plan that is running but will not renew  (a pass with no Stripe
 *     agreement, or one whose agreement is winding down)
 *   - a plan that has already ended              (`status: 'canceled'`)
 *
 * Treating the second as "no plan" hid a customer's own plan from them
 * (bug 22364); treating the third as a live plan would nudge an expired SMB
 * account to upgrade to Enterprise. Whether a plan RENEWS is a separate
 * question — read `auto_renew` directly for that.
 */

type PlanPeriod = Pick<SubscriptionData, 'current_period_end'>

/**
 * Has the paid period already run out?
 *
 * Deliberately lenient in both unknown directions: a pass with no end date
 * never expires, and an unparseable date is left alone rather than guessed
 * away — the server is the authority, this only stops the UI describing a
 * finished plan as the current one. The check is not redundant with the
 * server's own status: a pass is only flipped to `expired` when the sweep
 * runs, so one that ran out an hour ago can still come back as `active`.
 */
export function planHasEnded(subscription: PlanPeriod | null | undefined): boolean {
  if (!subscription?.current_period_end) return false
  const endMs = new Date(subscription.current_period_end).getTime()
  if (Number.isNaN(endMs)) return false
  return endMs <= Date.now()
}

/**
 * The subscription if a plan is actually running, otherwise null — so callers
 * can write `livePlan(subscription)?.plan.name` and get "no plan" for an
 * expired one without repeating the date arithmetic.
 */
export function livePlan(
  subscription: SubscriptionData | null | undefined,
): SubscriptionData | null {
  if (!subscription) return null
  // The server only ever sends `canceled` for a plan it has already finished
  // with, so take its word for it and don't re-litigate from the dates.
  if (subscription.status === 'canceled') return null
  return planHasEnded(subscription) ? null : subscription
}

/**
 * Is this plan a downgrade the customer may not take right now?
 *
 * Product rule (no-downgrade): while the current plan is still renewing, a
 * LOWER-tier plan cannot be selected — the customer cancels the running plan
 * first (it stays active to its expiry), then the smaller plan takes over.
 * Once the current plan is winding down or expired (`planRenews` false) the
 * lower plan is open again. Tier is the plan's monthly price, independent of
 * the billing cycle, so the lock reads the same on the monthly and annual
 * tabs and matches the server guard in POST /billing/switch-plan.
 *
 * Returns false when there is no live plan to downgrade from, for the current
 * plan itself, for a quoted plan (no self-serve rate), and for any same-or-
 * higher tier (an upgrade or cycle change is always allowed).
 */
export function isDowngradeLocked(args: {
  planRenews: boolean
  isCurrent: boolean
  isQuoted: boolean
  targetTierCents: number
  currentTierCents: number | null
}): boolean {
  const { planRenews, isCurrent, isQuoted, targetTierCents, currentTierCents } = args
  if (!planRenews || isCurrent || isQuoted || currentTierCents === null) return false
  return targetTierCents < currentTierCents
}
